import { FC, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import useBasePath from "src/utils/hooks/useBasePath";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { useModifyEmployeeBalances } from "src/Pages/Company/Absences/api/employee.queries";
import { getYearRange } from "src/utils/Company/Absences/checkExcludedWeekDays";
import { Loader } from "src/Components/Loader/Loader";
import QuantityInput from "src/Pages/Company/Absences/Forms/QuantityInput";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import {
  BillingCycleConfigurationType,
  EPatternUnit,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { BalanceActions } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import { createAbsenceRecordsModifyBalanceSchema } from "./AbsenceRecordsModifyBalance.schema";

interface IAbsenceRecordsModifyBalanceFormProps {
  employeeId: string;
  dailyWorkingHours: number;
  absenceType: IAbsenceType;
}

interface IFormValues {
  name: string;
  year: string;
  month?: string;
  days: number;
  hours: number;
  operation: BalanceActions;
  unit: EPatternUnit;
  note?: string;
}

export interface IParsedBalancesFormData
  extends Pick<IFormValues, "operation" | "note"> {
  absenceTypeId: string;
  timeValue: { days?: number } | { hours?: number };
  yearBillingCycle?: string;
  yearMonthBillingCycle?: string;
}

const AbsenceRecordsModifyBalanceForm: FC<
  IAbsenceRecordsModifyBalanceFormProps
> = ({ employeeId, dailyWorkingHours, absenceType }) => {
  const history = useHistory();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const basePath = useBasePath();
  const currentYear = new Date().getFullYear().toString();
  const availableYearOptions = getYearRange(currentYear, 5);

  const modifyEmployeeBalancesMutation = useModifyEmployeeBalances(employeeId);

  const absenceRecordsModifyBalanceSchema =
    createAbsenceRecordsModifyBalanceSchema(
      absenceType.billingCycleConfiguration?.type
    );

  const formMethods = useForm<IFormValues>({
    resolver: standardSchemaResolver(absenceRecordsModifyBalanceSchema),
    mode: "onChange",
    defaultValues: {
      name: absenceType.name,
      year: currentYear,
      operation: BalanceActions.ADD,
      unit: EPatternUnit.Day,
      days: 0,
      hours: 0,
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const chosenUnit = watch("unit");

  const confirmLeaving = useCallback(() => {
    if (typeof setModal === "function") {
      setModal({
        open: true,
        title: <>{__("Czy na pewno chcesz opuścić stronę?")}</>,
        description: __(
          "Opuszczenie tej strony spowoduje utratę wszystkich uzupełnionych danych. Czy na pewno chcesz kontynuować?"
        ),
        actions: [
          {
            label: __("Anuluj"),
            variant: "outlined",
            callbackFn: () => {
              resetModal();
            },
          },
          {
            label: __("Opuść stronę"),
            variant: "contained",
            callbackFn: () => {
              history.goBack();
              resetModal();
            },
          },
        ],
      });
    }
  }, [history, resetModal, setModal]);

  const onSubmit = (formData: IFormValues) => {
    const { name, year, month, unit, days, hours, ...rest } = formData;
    const parsedFormData: IParsedBalancesFormData = {
      ...rest,
      absenceTypeId: absenceType.id,
      timeValue: {
        ...(unit === EPatternUnit.Day && { days }),
        ...(unit === EPatternUnit.Hour && { hours }),
      },
    };

    // Depending on absenceType config - send year or year & month to API
    if (
      absenceType.billingCycleConfiguration?.type ===
      BillingCycleConfigurationType.Yearly
    ) {
      parsedFormData.yearBillingCycle = year;
    } else if (
      absenceType.billingCycleConfiguration?.type ===
      BillingCycleConfigurationType.Monthly
    ) {
      parsedFormData.yearMonthBillingCycle = `${year}-${month}`;
    }

    modifyEmployeeBalancesMutation.mutate(parsedFormData, {
      onSuccess: () => {
        dynamicNotification(
          __("Pomyślnie zmieniono wymiar nieobecności pracownika.")
        );

        history.push(`${basePath}/absences/employee/${employeeId}#balances`);
      },
      onError: () => {
        dynamicNotification(
          __("Nie udało się zmienić wymiaru nieobecności pracownika."),
          "error",
          "top-right",
          7000
        );
      },
    });
  };

  return (
    <Paper
      sx={{ p: "2rem" }}
      data-testid="absences-records-modify-balance-form"
    >
      <FormProvider {...formMethods}>
        <Fieldset
          legend={__("Dzienny wymiar czasu pracy pracownika")}
          helpText={__(
            "Dzienny wymiar świadczenia pracy przez pracownika podany w godzinach."
          )}
        >
          <Typography variant="body1">{dailyWorkingHours}</Typography>
        </Fieldset>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Fieldset legend={__("Rodzaj nieobecności")}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="name"
                  name="name"
                  label={__("Rodzaj wniosku")}
                  inputRef={field.ref}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  fullWidth
                  data-testid="absences-records-modify-balance-form-field__name"
                />
              )}
            />
          </Fieldset>
          <Fieldset legend={__("Wybierz okres")}>
            <Stack direction="row" spacing={4}>
              <FormControl fullWidth>
                <InputLabel id="year-label">{__("Rok")}</InputLabel>
                <Controller
                  name="year"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={__("Rok")}
                      labelId="year-label"
                      required
                      data-testid="absences-records-modify-balance-form-field__year"
                    >
                      {availableYearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="month-label">{__("Miesiąc")}</InputLabel>
                <Controller
                  name="month"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      label={__("Miesiąc")}
                      labelId="month-label"
                      disabled={
                        absenceType.billingCycleConfiguration?.type ===
                        BillingCycleConfigurationType.Yearly
                      }
                      error={!!error}
                      data-testid="absences-records-modify-balance-form-field__month"
                    >
                      {availableMonthsOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error={!!errors?.month}>
                  {absenceType.billingCycleConfiguration?.type ===
                    BillingCycleConfigurationType.Yearly &&
                    __(
                      "Dla wniosków rozliczanych rocznie możliwe jest wskazanie tylko roku."
                    )}
                  {errors?.month ? __(errors?.month?.message) : null}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Fieldset>
          <Fieldset
            legend={__("Zmień wymiar nieobecności pracownika")}
            helpText={
              <>
                <p>
                  {__(
                    "Dodanie i odjęcie dni spowoduje zmianę wymiaru nieobecności pracownika. Dodane dni zostaną przeliczone na godziny zgodnie z dziennym wymiarem pracy. Dodanie godzin służy korekcie salda godzinowego w widoku pracownika."
                  )}
                </p>
                <p>
                  {__(
                    "Dodane godziny nie zmieniają wymiaru nieobecności oraz nie zostaną przeliczone na dni nieobecności zgodne z wymiarem pracy."
                  )}
                </p>
              </>
            }
          >
            <Typography variant="body2" color="text.secondary" mb="0.5rem">
              {__("Dodaj lub odbierz dni")}:
            </Typography>
            <FormControl required fullWidth>
              <Controller
                name="operation"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    name="operation"
                    data-testid="absences-records-modify-balance-form-field__operation"
                  >
                    <FormControlLabel
                      value={BalanceActions.ADD}
                      label={__("Dodaj")}
                      control={<Radio />}
                    />
                    <FormControlLabel
                      value={BalanceActions.REMOVE}
                      label={__("Odbierz")}
                      control={<Radio />}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ marginTop: "1rem" }}>
              <Typography variant="body2" color="text.secondary" mb="0.5rem">
                {__("Dodaj dni / godziny")}:
              </Typography>
              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    name="unit"
                    data-testid="absences-records-modify-balance-form-field__unit"
                  >
                    <FormControlLabel
                      value={EPatternUnit.Day}
                      label={__("Dni")}
                      control={<Radio />}
                    />
                    <FormControlLabel
                      value={EPatternUnit.Hour}
                      label={__("Godziny")}
                      control={<Radio />}
                    />
                  </RadioGroup>
                )}
              />
              {chosenUnit === EPatternUnit.Day ? (
                <QuantityInput
                  name="days"
                  min={0}
                  max={100}
                  data-testid="absences-records-modify-balance-form-field__days"
                />
              ) : (
                <QuantityInput
                  name="hours"
                  min={0}
                  max={100}
                  data-testid="absences-records-modify-balance-form-field__hours"
                />
              )}
            </FormControl>
          </Fieldset>
          <Fieldset
            legend={__(
              "Dodaj informacje z jakiego powodu dokonujesz zmiany na koncie pracownika"
            )}
          >
            <ControlledTextField
              name="note"
              label={__("Notatka")}
              multiline
              maxRows={3}
              maxLength={255}
              data-testid="absences-records-modify-balance-form-field__note"
            />
          </Fieldset>
          <Box
            sx={{
              textAlign: "right",
              margin: "2rem 0 0 1rem",
            }}
          >
            <Button
              variant="outlined"
              type="button"
              size="large"
              sx={{ marginRight: "1rem" }}
              onClick={() => confirmLeaving()}
              disabled={modifyEmployeeBalancesMutation.isPending}
            >
              {__("Anuluj")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={modifyEmployeeBalancesMutation.isPending}
              data-testid="absences-records-modify-balance-form-field__button-submit"
            >
              {__("Zatwierdź")}
            </Button>
          </Box>
        </form>
      </FormProvider>
      {modifyEmployeeBalancesMutation.isPending && <Loader />}
    </Paper>
  );
};

export default AbsenceRecordsModifyBalanceForm;

const availableMonthsOptions = [
  { label: "Styczeń", value: "01" },
  { label: "Luty", value: "02" },
  { label: "Marzec", value: "03" },
  { label: "Kwiecień", value: "04" },
  { label: "Maj", value: "05" },
  { label: "Czerwiec", value: "06" },
  { label: "Lipiec", value: "07" },
  { label: "Sierpień", value: "08" },
  { label: "Wrzesień", value: "09" },
  { label: "Październik", value: "10" },
  { label: "Listopad", value: "11" },
  { label: "Grudzień", value: "12" },
];
